// Tracks list — broadcast-graphics card grid. Reuses the home page's
// fixed nav and visual language via `body.home-route`, so the global
// topbar hides and we get the full viewport. Each track gets a card
// with a placeholder thumbnail (per-track screenshots will live on
// the track schema later); the first card is a "+ New track" tile.

import { listTracks, setSelectedTrack, clearSelectedTrack } from '../api.js';

// Per-id thumbnail overrides — until tracks carry their own preview
// in the schema, we hand-pick screenshots for the bundled fixtures.
// Anything not listed falls through to a gradient placeholder.
const TRACK_THUMBS = {
  'brands-hatch': './homepage-images/BrandsHatchEditScreen.jpg',
  'midohio':      './homepage-images/MidOhioEditScreen.jpg',
};

export async function render(mountEl) {
  document.body.classList.add('home-route');

  mountEl.innerHTML = `
    <div class="home-root tracks-root">
      <nav class="home-nav">
        <span class="home-brand">Apex Slayer <span class="sub">// Race Engineer</span></span>
        <div class="home-nav-spacer"></div>
        <a href="#/play" class="home-nav-link">Pit Lane</a>
        <a href="#/roster" class="home-nav-link">Roster</a>
      </nav>

      <section class="play-hero">
        <div class="home-section-label">// Garage</div>
        <h1 class="play-h1">Tracks <span class="accent">·</span> Catalog</h1>
        <p class="play-subtitle">Pick a circuit to race or edit. Build a new one from scratch — drop in a satellite image and trace the line.</p>
      </section>

      <section class="tracks-grid" id="tracks-grid">
        <div class="tracks-loading">Loading tracks…</div>
      </section>

      <div class="play-spacer"></div>
    </div>
  `;

  const gridEl = mountEl.querySelector('#tracks-grid');

  try {
    const tracks = await listTracks();
    gridEl.innerHTML = renderNewTile() + tracks.map(renderCard).join('');
  } catch (err) {
    gridEl.innerHTML = `
      ${renderNewTile()}
      <div class="tracks-error">Failed to load tracks: ${escapeHtml(err.message)}</div>
    `;
  }

  // Track selection happens via sessionStorage so the URL stays clean
  // (`#/edit`, `#/race-setup`, no `?track=<id>` payload). One delegated
  // click handler covers all the cards — the buttons carry data-target
  // ("race" or "edit") and data-track-id, the new-track tile carries
  // data-target="new" and clears the selection.
  gridEl.addEventListener('click', e => {
    const a = e.target.closest('[data-target]');
    if (!a) return;
    const target  = a.dataset.target;
    const trackId = a.dataset.trackId;
    if (target === 'new') {
      clearSelectedTrack();
      window.location.hash = '#/edit';
    } else if (target === 'race' && trackId) {
      setSelectedTrack(trackId);
      window.location.hash = '#/race-setup';
    } else if (target === 'edit' && trackId) {
      setSelectedTrack(trackId);
      window.location.hash = '#/edit';
    } else {
      return;
    }
    e.preventDefault();
  });

  return () => {
    document.body.classList.remove('home-route');
  };
}

function renderNewTile() {
  return `
    <a class="track-card track-card-new" href="#/edit" data-target="new">
      <div class="track-card-thumb track-card-thumb-new">
        <div class="track-card-plus">+</div>
        <div class="track-card-new-label">New track</div>
      </div>
      <div class="track-card-body">
        <div class="track-card-num">/ 00</div>
        <div class="track-card-title">Build a circuit</div>
        <div class="track-card-desc">Open the editor with a blank canvas. Drop a satellite image and start tracing.</div>
        <div class="track-card-cta">Open editor <span class="arrow">→</span></div>
      </div>
    </a>
  `;
}

function renderCard(t, idx) {
  const thumbSrc = TRACK_THUMBS[t.id];
  const num = String(idx + 1).padStart(2, '0');

  // Per-card hue — used by the gradient-fallback only — gives each
  // card a distinct visual fingerprint until real thumbnails land.
  const hue = (idx * 47) % 360;

  const thumbContent = thumbSrc
    ? `<img src="${escapeAttr(thumbSrc)}" alt="${escapeAttr(t.name)} preview" onerror="this.parentElement.classList.add('track-card-thumb-fallback');this.remove()">`
    : '';

  // Buttons stay as <a href="#/...">: gives keyboard / right-click /
  // open-in-new-tab semantics. The delegated click handler in render()
  // intercepts left-clicks to stash the track in sessionStorage before
  // letting the route resolve; right-clicks fall through to the bare
  // hash and the receiving page's "no track" branch will redirect.
  return `
    <article class="track-card">
      <div class="track-card-thumb${thumbSrc ? '' : ' track-card-thumb-fallback'}" style="--card-hue:${hue}">
        ${thumbContent}
        <div class="track-card-thumb-name">${escapeHtml(t.name)}</div>
      </div>
      <div class="track-card-body">
        <div class="track-card-num">/ ${num}</div>
        <div class="track-card-title">${escapeHtml(t.name)}</div>
        ${t.author ? `<div class="track-card-author">by ${escapeHtml(t.author)}</div>` : ''}
        ${t.description ? `<div class="track-card-desc">${escapeHtml(t.description)}</div>` : '<div class="track-card-desc track-card-desc-empty">No description.</div>'}
        <div class="track-card-actions">
          <a class="home-btn track-card-btn" href="#/race-setup" data-target="race" data-track-id="${escapeAttr(t.id)}">Race <span class="arrow">→</span></a>
          <a class="home-btn track-card-btn secondary" href="#/edit" data-target="edit" data-track-id="${escapeAttr(t.id)}">Edit</a>
        </div>
      </div>
    </article>
  `;
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
function escapeAttr(s) { return escapeHtml(s); }
